import * as React from "react"
import { NavLink } from "react-router-dom"
import {
  Home,
  Receipt,
  Users,
  Tags,
  Settings,
  Plus,
  Menu,
  X,
  LogOut,
  Activity,
  Repeat
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { GroupSelector } from "@/components/groups/group-selector"
import type { User as UserType, Group } from "@/types/domain.types"

interface SidebarProps {
  user: UserType
  currentGroup?: Group
  isOpen: boolean
  onToggle: () => void
  onLogout: () => void
  className?: string
}

interface NavItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  badge?: string | number
}

const mainNavItems: NavItem[] = [
  { name: "Dashboard", href: "/", icon: Home },
  { name: "Transactions", href: "/expenses", icon: Receipt },
  { name: "Recurring", href: "/recurring", icon: Repeat },
  { name: "Activity", href: "/activity", icon: Activity },
  { name: "Groups", href: "/groups", icon: Users },
  { name: "Categories", href: "/categories", icon: Tags },
]

const bottomNavItems: NavItem[] = [
  { name: "Settings", href: "/settings", icon: Settings },
]

export const Sidebar: React.FC<SidebarProps> = ({
  user,
  currentGroup,
  isOpen,
  onToggle,
  onLogout,
  className,
}) => {


  const NavItemComponent: React.FC<{
    item: NavItem
    onClick?: () => void
  }> = ({ item, onClick }) => (
    <NavLink
      to={item.href}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-lg text-sm font-medium transition-colors",
          isOpen ? "px-3 py-2" : "px-2 py-2 justify-center",
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:text-foreground hover:bg-muted"
        )
      }
    >
      <item.icon className="h-4 w-4 flex-shrink-0" />
      {isOpen && (
        <>
          <span className="flex-1">{item.name}</span>
          {item.badge && (
            <Badge variant="secondary" className="text-xs">
              {item.badge}
            </Badge>
          )}
        </>
      )}
    </NavLink>
  )

  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        {isOpen && (
          <div className="flex items-center gap-2">
            <Receipt className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg">Moitie</span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="h-8 w-8"
        >
          {isOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>
      </div>

      {/* Group Selector - Mobile Only */}
      {isOpen && (
        <div className="p-4 border-b lg:hidden">
          <div className="text-xs text-muted-foreground mb-2">Select Group</div>
          <GroupSelector isMobile />
        </div>
      )}

      {/* Current Group - Desktop Only */}
      {isOpen && currentGroup && (
        <div className="p-4 border-b hidden lg:block">
          <div className="text-xs text-muted-foreground mb-1">Current Group</div>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              {currentGroup.emoji ? (
                <span className="text-sm">{currentGroup.emoji}</span>
              ) : (
                <Users className="h-4 w-4 text-primary" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{currentGroup.name}</div>
              <div className="text-xs text-muted-foreground">
                {currentGroup.members?.length || 0} members
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {isOpen && (
        <div className="p-4 border-b">
          <div className="text-xs text-muted-foreground mb-2">Quick Actions</div>
          <div className="space-y-1">
            <Button variant="outline" size="sm" className="w-full justify-start">
              <Plus className="mr-2 h-3 w-3" />
              Add Expense
            </Button>
          </div>
        </div>
      )}

      {/* Main Navigation */}
      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {mainNavItems.map((item) => (
            <NavItemComponent
              key={item.href}
              item={item}
              onClick={() => {
                // Close sidebar on mobile after navigation
                if (window.innerWidth < 768) {
                  onToggle()
                }
              }}
            />
          ))}
        </div>
      </nav>

      {/* Bottom Navigation */}
      <div className="p-4 border-t">
        <div className="space-y-1 mb-4">
          {bottomNavItems.map((item) => (
            <NavItemComponent
              key={item.href}
              item={item}
              onClick={() => {
                if (window.innerWidth < 768) {
                  onToggle()
                }
              }}
            />
          ))}
        </div>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                "w-full p-2 h-auto",
                isOpen ? "justify-start" : "justify-center"
              )}
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={user.avatar_url} />
                <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
              </Avatar>
              {isOpen && (
                <div className="ml-3 text-left">
                  <div className="font-medium text-sm">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </div>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed top-0 left-0 z-50 h-full bg-background border-r transition-all duration-300 lg:relative lg:translate-x-0",
          isOpen ? "w-64 translate-x-0" : "w-16 -translate-x-full lg:translate-x-0",
          className
        )}
      >
        {sidebarContent}
      </div>
    </>
  )
}
